import { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
  Pagination,
  Stack,
  Grid,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import TuneIcon from '@mui/icons-material/Tune';

import nodatafound from '../../utilities/dummy_assets/nodatafound.jpg';
import MachineListAPIs from '../../utilities/api/machine/AdminMachineList';
import MachineDetailAPIs from '../../utilities/api/machine/AdminMachineDetail';
import { useSelector } from 'react-redux';
import EditMachineDialog from './Popups/EditMachine';
import Delete from './Popups/Delete';
import DatePickerDialog from './Popups/DatePicker';
import CustomPagination from '../CustomPagination/CustomPagination';
import { ImageBaseURL } from '../../utilities/api/axios';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CommonInventoryCard from './CommonInventoryCard';

export const dataGridSx = {
  '& .MuiDataGrid-root': {
    border: 'none',
  },
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: '#f5f5f5',
    color: '#3f3f3f',
    fontWeight: 'bold',
    fontSize: '16px',
    textAlign: 'left',
  },
  '& .MuiDataGrid-columnHeader': {
    borderRight: '1px solid #E6E6E6',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiDataGrid-cell': {
    textAlign: 'left',
    color: '#3f3f3f',
    borderRight: '1px solid #E6E6E6',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: '4px',
    paddingBottom: '4px',
    borderTop: 'none',
    '& .MuiTablePagination-root': {
      textAlign: 'center',
      '& .MuiButtonBase-root': {
        color: '#3f3f3f',
      },
    },
  },
  '& .MuiPaginationItem-root': {
    color: '#3f3f3f',
  },
  '& .MuiDataGrid-selectedRowCount': {
    visibility: 'hidden',
    width: 0,
    padding: 0,
    margin: 0,
    flex: '0 !important',
  },
};

/* ------- Card Fields -----*/

const machineFields = [
  { label: 'Category', value: 'category', type: 'chip' },
  { label: 'Stock', value: 'instances' },
  { label: 'Status', value: 'status' },
  { label: 'Supervised', value: r => (r.supervised ? 'Yes' : 'No') },
  { label: 'Availability', value: 'availability' },
  { label: 'Description', value: 'description', type: 'tooltip' },
];

/* ------------------- DataGrid Columns ------------------- */

const columns = (handleEditInventory, handleOpenDeletePopup) => [
  {
    field: 'name',
    headerName: 'Machine Name',
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    minWidth: 200,
    flex: 1.5,
    renderCell: params => {
      const imageUrl = params.row?.image
        ? params.row.image.startsWith('http')
          ? params.row.image
          : `${ImageBaseURL}${params.row.image}`
        : nodatafound;
      const text = params.row?.name || 'No Text';
      const subtext = params.row?.upc || 'No Subtext';

      return (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <img
            src={imageUrl}
            alt={text}
            style={{ width: '50px', height: '50px', borderRadius: '50%' }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ color: '#434343' }}>
              {text}
            </Typography>
            <Typography variant="body2" sx={{ color: '#8C8C8C' }}>
              {subtext}
            </Typography>
          </Box>
        </Box>
      );
    },
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box
        sx={{
          border: '1px solid #E6E6E6',
          borderRadius: '50px',
          padding: '4px 8px',
        }}
      >
        <Typography sx={{ color: '#8C8C8C', textAlign: 'left', width: '100%' }}>
          {params.row.category}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'instances',
    headerName: 'Stock',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.instances}
      </Typography>
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.status}
      </Typography>
    ),
  },
  {
    field: 'Supervised',
    headerName: 'Supervised',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.supervised ? 'Yes' : 'No'}
      </Typography>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 200,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography
        sx={{
          color: '#434343',
          textAlign: 'left',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={params.row.description || '-'}
      >
        {params.row.description || '-'}
      </Typography>
    ),
  },
  {
    field: 'availability',
    headerName: 'Availability',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.availability}
      </Typography>
    ),
  },
  {
    field: 'action',
    headerName: 'Action',
    sortable: false,
    width: 150,
    headerAlign: 'left',
    align: 'center',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <IconButton
          aria-label="edit"
          onClick={() => handleEditInventory(params.row)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          aria-label="delete"
          onClick={() => handleOpenDeletePopup(params.row)}
        >
          <DeleteOutlineSharpIcon fontSize="small" sx={{ color: '#C90505' }} />
        </IconButton>
      </Box>
    ),
  },
];

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <img
      src={nodatafound}
      alt="No Data Found"
      style={{ width: '250px', marginBottom: '8px' }}
    />
    <Typography variant="body1" color="textSecondary">
      No data available
    </Typography>
  </Box>
);

const CustomLoadingOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <CircularProgress size={40} />
    <Typography sx={{ mt: 2 }} color="textSecondary">
      Updating list...
    </Typography>
  </Box>
);

const MachineTable = ({
  searchQuery,
  category,
  update,
  setUpdate,
  flag,
  setFlag,
  setTableDataProp,
  status,
  supervised,
}) => {
  const user = useSelector(state => state.user.user);
  const token = user.access;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);
  const [isEditInventoryOpen, setEditInventoryOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [date, setDate] = useState(new Date());

  // ✅ 1. Reset page to 0 whenever search query or category changes
  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchQuery, category, status, supervised]);

  // ✅ 2. Fetch data from server based on pagination, search, and category
  useEffect(() => {
    setLoading(true);

    MachineListAPIs.MachineListGet({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: searchQuery,
      category: category,
      status: status,
      supervised: supervised,
    })
      .then(result => {
        // Backend returns { count, next, previous, results }
        const data = result.results || [];
        setTableData(data);
        setRowCount(result.count || 0);
        if (setTableDataProp) setTableDataProp(data);
      })
      .catch(() => toast.error('Error fetching machine data'))
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    searchQuery,
    category,
    status,
    supervised,
    update,
    user,
  ]);

  // Rows for DataGrid (No longer filtering locally)
  const rows = tableData.map((item, index) => ({ ...item, index }));

  const handleEditInventory = row => {
    setCurrentRow(row);
    setEditInventoryOpen(true);
  };
  const handleCloseDatePicker = () => {
    setIsDatePickerOpen(false);
  };

  const handleDateChange = newDate => {
    setDate(newDate);
  };
  const handleOpenDeletePopup = row => {
    setCurrentRow(row);
    setOpenDeletePopup(true);
  };
  const handleCloseDeletePopup = () => {
    setOpenDeletePopup(false);
  };

  const handleDelete = () => {
    MachineDetailAPIs.MachineDetailDelete(currentRow.id, token)
      .then(() => {
        setUpdate(prev => !prev);
        toast.success('Item successfully deleted');
      })
      .catch(() => toast.error('Error deleting item'));

    setOpenDeletePopup(false);
  };

  /* ------------------- Card Pagination ------------------- */
  const cardsPerPage = paginationModel.pageSize;
  const totalPages = Math.ceil(rowCount / cardsPerPage);

  return (
    <Box
      sx={{
        width: '100%',
        background: '#fff',
        borderRadius: '12px',
        p: 2,
        minHeight: '550px',
      }}
    >
      {isMobile ? (
        /* ------------------- MOBILE/TABLET CARDS ------------------- */
        <Box position="relative" sx={{ minHeight: '400px' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                minHeight: '200px',
              }}
            >
              <CircularProgress size={40} />
              <Typography mt={2}>Updating list...</Typography>
            </Box>
          )}

          {rows.length === 0 && !loading ? (
            <Box
              textAlign="center"
              py={10}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img src={nodatafound} alt="no-data" width="250" />
              <Typography color="textSecondary" mt={1}>
                No data available
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {rows.map(row => (
                  <Grid item key={row.id} xs={12} sm={6} md={4}>
                    <CommonInventoryCard
                      row={row}
                      fields={machineFields}
                      onEdit={handleEditInventory}
                      onDelete={handleOpenDeletePopup}
                    />
                  </Grid>
                ))}
              </Grid>

              <Stack alignItems="center" mt={2}>
                <Pagination
                  count={totalPages}
                  page={paginationModel.page + 1}
                  onChange={(e, value) =>
                    setPaginationModel(prev => ({ ...prev, page: value - 1 }))
                  }
                  color="primary"
                />
              </Stack>
            </>
          )}
        </Box>
      ) : (
        /* ------------------- DESKTOP TABLE ------------------- */
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={rows}
            rowHeight={85}
            columns={columns(handleEditInventory, handleOpenDeletePopup)}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[7, 10, 25]}
            slots={{
              pagination: CustomPagination,
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: CustomLoadingOverlay,
              columnMenuIcon: () => (
                <TuneIcon
                  sx={{
                    transform: 'rotate(90deg)',
                    fill: '#8C8C8C',
                    height: 20,
                  }}
                />
              ),
            }}
            disableDensitySelector
            disableExport
            disableMultipleColumnsSorting
            sortingOrder={[]}
            disableColumnSort
            sx={dataGridSx}
          />
        </Box>
      )}
      <Delete
        open={openDeletePopup}
        onClose={handleCloseDeletePopup}
        onConfirm={handleDelete}
      />
      <EditMachineDialog
        currentRow={currentRow}
        open={isEditInventoryOpen}
        onClose={() => setEditInventoryOpen(false)}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
      />

      <DatePickerDialog
        open={isDatePickerOpen}
        selectedDate={date}
        handleDateChange={handleDateChange}
        handleClose={handleCloseDatePicker}
      />
      <ToastContainer />
    </Box>
  );
};

export default MachineTable;
